import { useCreateBrandLanguageMutation } from "entities/brands/languages/mutations/useCreateBrandLanguageMutation";
import { useUpdateBrandLanguageMutation } from "entities/brands/languages/mutations/useUpdateBrandLanguageMutation";
import {
  invalidateBrandLanguagesQuery,
  useBrandLanguagesQuery,
} from "entities/brands/languages/queries/useBrandLanguagesQuery";
import { useCreateLanguageAssistantMutation } from "entities/languagesAssistants/mutations/mutateCreateLanguageAssistant";
import { useUpdateGameNameAssistantMutation } from "entities/languagesAssistants/mutations/mutateUpdateGameNameAssistant";
import { useUpdateGeneralAssistantMutation } from "entities/languagesAssistants/mutations/mutateUpdateGeneralAssistant";
import {
  invalidateLanguageAssistantsQuery,
  useLanguageAssistantsQuery,
} from "entities/languagesAssistants/queries/useLanguageAssistantsQuery";
import langs from "langs";
import React, { FC, useEffect, useMemo, useState, useCallback } from "react";
import useInputState, { InputHookResult } from "shared/hooks/useInputState";
import { LocaleCode } from "shared/types/languages";
import { createContext } from "shared/utils/context";
import { useDeleteAssistants } from "../hooks/useDeleteAssistants";
import {
  LanguageDetailsType,
  LanguageDetailsProviderProps,
  LanguageDetails,
} from "./types";

const [Provider, useLanguageDetails] = createContext<LanguageDetailsType>({
  name: "languageDetailsContext",
  hookName: "useLanguageDetails",
  providerName: "languageDetailsProvider",
});

const LanguageDetailsProvider: FC<LanguageDetailsProviderProps> = ({
  children,
  drawerDetails,
  brandId,
}) => {
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageDetails>({
    label: "",
    value: "",
  });
  const displayNameInput: InputHookResult = useInputState("displayName", "");
  const generalTranslationIdInput: InputHookResult = useInputState(
    "generalTranslationId",
    "",
  );
  const gameNameTranslationIdInput: InputHookResult = useInputState(
    "gameNameTranslationId",
    "",
  );

  const { data: initialLanguageAssistants = [] } = useLanguageAssistantsQuery();
  const { data: languages = [] } = useBrandLanguagesQuery(brandId);

  const {
    mutateAsync: createLanguageAssistant,
    isPending: isLoadingCreateLanguageAssistant,
    isSuccess: isCreatedLanguageAssistant,
  } = useCreateLanguageAssistantMutation(selectedLanguage.value);
  const {
    mutateAsync: createBrandLanguage,
    isPending: isLoadingCreateBrandLanguage,
    isSuccess: isCreatedBrandLanguage,
  } = useCreateBrandLanguageMutation(selectedLanguage.value);
  const {
    mutateAsync: updateBrandLanguage,
    isPending: isUpdatingBrandLanguage,
  } = useUpdateBrandLanguageMutation();

  const { mutateAsync: updateGameNameAssistant } =
    useUpdateGameNameAssistantMutation();
  const { mutateAsync: updateGeneralAssistant } =
    useUpdateGeneralAssistantMutation();

  const { handleDeleteAssistants } = useDeleteAssistants();

  const allLanguageOptions = useMemo(() => {
    return langs.all().map((lang) => {
      return {
        value: lang[1],
        label: lang.name,
      };
    });
  }, []);

  useEffect(() => {
    const gameNameAssistantId =
      drawerDetails.drawerDetails?.gameNameAssistantId;
    const generalAssistantId = drawerDetails.drawerDetails?.generalAssistantId;

    const initialLanguageAssistant = initialLanguageAssistants.find(
      (assistant) => assistant.code === drawerDetails.drawerDetails?.code,
    );

    if (drawerDetails.drawerDetails?.id) {
      const { code, displayName } = drawerDetails.drawerDetails;
      const language = allLanguageOptions.find(
        (option) => option.value === code,
      );

      setSelectedLanguage({
        label: language?.label || "",
        value: code,
      });

      displayNameInput.setValue(displayName);

      if (initialLanguageAssistant) {
        const { generalAssistantId, gameNameAssistantId } =
          initialLanguageAssistant;
        generalTranslationIdInput.setValue(generalAssistantId);
        gameNameTranslationIdInput.setValue(gameNameAssistantId);
      }
    } else {
      if (gameNameAssistantId) {
        gameNameTranslationIdInput.setValue(gameNameAssistantId);
      }

      if (generalAssistantId) {
        generalTranslationIdInput.setValue(generalAssistantId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allLanguageOptions,
    drawerDetails.drawerDetails?.id,
    initialLanguageAssistants,
  ]);

  const selectedExistedLanguageAssistants = useMemo(() => {
    return initialLanguageAssistants.find(
      (assistant) => assistant.code === selectedLanguage.value,
    );
  }, [initialLanguageAssistants, selectedLanguage.value]);

  useEffect(() => {
    if (selectedExistedLanguageAssistants) {
      gameNameTranslationIdInput.setValue(
        selectedExistedLanguageAssistants.gameNameAssistantId,
      );
      generalTranslationIdInput.setValue(
        selectedExistedLanguageAssistants.generalAssistantId,
      );
    } else {
      if (drawerDetails.drawerDetails?.gameNameAssistantId) {
        gameNameTranslationIdInput.setValue(
          drawerDetails.drawerDetails?.gameNameAssistantId,
        );
      }

      if (drawerDetails.drawerDetails?.generalAssistantId) {
        generalTranslationIdInput.setValue(
          drawerDetails.drawerDetails?.generalAssistantId,
        );
      }
    }
  }, [
    gameNameTranslationIdInput,
    generalTranslationIdInput,
    selectedExistedLanguageAssistants,
    drawerDetails.drawerDetails,
  ]);

  const isEnglishSelectedLanguage = useMemo(() => {
    return selectedLanguage.value === LocaleCode.en;
  }, [selectedLanguage.value]);

  const existedLanguages = useMemo(
    () => languages.map((language) => language.code),
    [languages],
  );

  const handleClearData = useCallback(() => {
    setSelectedLanguage({ label: "", value: "" });
    displayNameInput.setValue("");
    generalTranslationIdInput.setValue("");
    gameNameTranslationIdInput.setValue("");
  }, [
    displayNameInput,
    generalTranslationIdInput,
    gameNameTranslationIdInput,
    setSelectedLanguage,
  ]);

  const onDeleteDefaultAssistants = useCallback(() => {
    const defaultGeneralAssistantId =
      drawerDetails.drawerDetails?.generalAssistantId;
    const defaultGameNameAssistantId =
      drawerDetails.drawerDetails?.gameNameAssistantId;

    if (
      defaultGeneralAssistantId &&
      defaultGameNameAssistantId &&
      selectedExistedLanguageAssistants?.generalAssistantId &&
      selectedExistedLanguageAssistants?.gameNameAssistantId
    ) {
      handleDeleteAssistants({
        generalAssistantId: defaultGeneralAssistantId,
        gameNameAssistantId: defaultGameNameAssistantId,
      });
    }
  }, [
    drawerDetails.drawerDetails?.generalAssistantId,
    drawerDetails.drawerDetails?.gameNameAssistantId,
    selectedExistedLanguageAssistants?.generalAssistantId,
    selectedExistedLanguageAssistants?.gameNameAssistantId,
    handleDeleteAssistants,
  ]);

  const onDeleteAssistants = useCallback(async () => {
    const isConfirmedDelete = window.confirm(
      "Are you sure you want to proceed? This action cannot be undone.",
    );

    const defaultGeneralAssistantId =
      drawerDetails.drawerDetails?.generalAssistantId;
    const defaultGameNameAssistantId =
      drawerDetails.drawerDetails?.gameNameAssistantId;

    const deleteGeneralAssistantId =
      selectedExistedLanguageAssistants?.generalAssistantId ||
      defaultGeneralAssistantId;

    const deleteGameNameAssistantId =
      selectedExistedLanguageAssistants?.gameNameAssistantId ||
      defaultGameNameAssistantId;

    if (
      isConfirmedDelete &&
      deleteGeneralAssistantId &&
      deleteGameNameAssistantId
    ) {
      handleDeleteAssistants({
        generalAssistantId: deleteGeneralAssistantId,
        gameNameAssistantId: deleteGameNameAssistantId,
      });
      drawerDetails.closeDrawer();
      handleClearData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    drawerDetails.drawerDetails?.gameNameAssistantId,
    drawerDetails.drawerDetails?.generalAssistantId,
    selectedExistedLanguageAssistants?.generalAssistantId,
    selectedExistedLanguageAssistants?.gameNameAssistantId,
    handleDeleteAssistants,
    handleClearData,
  ]);

  const handleCloseDrawer = useCallback(() => {
    const shouldDeleteAssistantsForNewLanguage = Boolean(
      !drawerDetails.drawerDetails?.id &&
        !isCreatedLanguageAssistant &&
        !isCreatedBrandLanguage &&
        !isLoadingCreateLanguageAssistant &&
        !isLoadingCreateBrandLanguage &&
        gameNameTranslationIdInput.input.value &&
        generalTranslationIdInput.input.value,
    );

    if (shouldDeleteAssistantsForNewLanguage) {
      return onDeleteAssistants();
    }

    handleClearData();
    drawerDetails.closeDrawer();
  }, [
    drawerDetails,
    isCreatedLanguageAssistant,
    isCreatedBrandLanguage,
    isLoadingCreateLanguageAssistant,
    isLoadingCreateBrandLanguage,
    gameNameTranslationIdInput.input.value,
    generalTranslationIdInput.input.value,
    handleClearData,
    onDeleteAssistants,
  ]);

  const handleUpdateLanguage = async () => {
    if (!drawerDetails.drawerDetails?.id) return;

    await updateBrandLanguage(
      {
        brandId,
        languageId: drawerDetails.drawerDetails.id,
        language: {
          displayName: displayNameInput.input.value,
        },
      },
      {
        onSuccess: () => {
          invalidateBrandLanguagesQuery(brandId);
        },
      },
    );
  };

  const onCreateBrandLanguageWithExistedAssistants = async () => {
    try {
      onDeleteDefaultAssistants();
      await createBrandLanguage(
        {
          brandId,
          language: {
            code: selectedLanguage.value,
            displayName: displayNameInput.input.value,
          },
        },
        {
          onSuccess: () => {
            invalidateBrandLanguagesQuery(brandId);
            invalidateLanguageAssistantsQuery();
          },
        },
      );
    } catch (error) {
      console.error(error);
    }
  };

  const handleLanguagesInvalidations = () => {
    invalidateBrandLanguagesQuery(brandId);
    invalidateLanguageAssistantsQuery();
  };

  const handleCreateLanguage = async () => {
    if (isEnglishSelectedLanguage) {
      await createBrandLanguage({
        brandId,
        language: {
          code: selectedLanguage.value,
          displayName: displayNameInput.input.value,
        },
      });

      const initialGeneralAssistantId =
        drawerDetails.drawerDetails?.generalAssistantId;
      const initialGameNameAssistantId =
        drawerDetails.drawerDetails?.gameNameAssistantId;

      if (initialGeneralAssistantId && initialGameNameAssistantId) {
        handleDeleteAssistants({
          generalAssistantId: initialGeneralAssistantId,
          gameNameAssistantId: initialGameNameAssistantId,
        });
      }
      handleLanguagesInvalidations();
      return;
    }

    if (
      selectedExistedLanguageAssistants?.generalAssistantId &&
      selectedExistedLanguageAssistants?.gameNameAssistantId
    ) {
      await onCreateBrandLanguageWithExistedAssistants();
      handleLanguagesInvalidations();
      return;
    }

    try {
      await Promise.all([
        createLanguageAssistant({
          code: selectedLanguage.value,
          name: selectedLanguage.label,
          generalAssistantId: generalTranslationIdInput.input.value,
          gameNameAssistantId: gameNameTranslationIdInput.input.value,
        }),
        createBrandLanguage({
          brandId,
          language: {
            code: selectedLanguage.value,
            displayName: displayNameInput.input.value,
          },
        }),
        updateGameNameAssistant({
          assistantId: gameNameTranslationIdInput.input.value,
          languageName: selectedLanguage.label,
        }),

        updateGeneralAssistant({
          assistantId: generalTranslationIdInput.input.value,
          languageName: selectedLanguage.label,
        }),
      ]).then(handleLanguagesInvalidations);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmitLanguage = async () => {
    try {
      if (drawerDetails.drawerDetails?.id) {
        await handleUpdateLanguage();
      } else {
        await handleCreateLanguage();
      }

      handleClearData();
      drawerDetails.closeDrawer();
    } catch (error) {
      console.error(error);
    }
  };

  const isDisabledSubmit = !Boolean(
    displayNameInput.input.value &&
      selectedLanguage &&
      generalTranslationIdInput.input.value &&
      gameNameTranslationIdInput.input.value,
  );

  const isSubmitting =
    isLoadingCreateBrandLanguage ||
    isLoadingCreateLanguageAssistant ||
    isUpdatingBrandLanguage;

  const isDisabledLanguageSelection = !!drawerDetails.drawerDetails?.code;

  const value = {
    languageDetailsDrawer: drawerDetails,
    displayNameInput,
    selectedLanguage,
    setSelectedLanguage,
    generalTranslationIdInput,
    gameNameTranslationIdInput,
    handleSaveLanguage: handleSubmitLanguage,
    isDisabledSubmit,
    isSubmitting,
    handleCloseDrawer,
    isDisabledLanguageSelection,
    existedLanguages,
    shouldHideAssistantFields: isEnglishSelectedLanguage,
    allLanguageOptions,
  };

  return <Provider value={value}>{children}</Provider>;
};

export { LanguageDetailsProvider, useLanguageDetails };
